import styled from '@emotion/styled';
import Accordion from '@ep/components/Accordion';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import Flex from '@ep/components/Flex';
import FacebookIcon from '@ep/images/facebook.svg';
import InstagramIcon from '@ep/images/instagram.svg';
import LinkedInIcon from '@ep/images/linkedin.svg';
import TwitterIcon from '@ep/images/twitter.svg';
import YouTubeIcon from '@ep/images/youtube.svg';
import theme from '@ep/theme';
import { FooterMenuData } from '@ep/types/menuData';
import { graphql, useStaticQuery } from 'gatsby';
import flatten from 'lodash.flatten';
import React, { Fragment } from 'react';
import FooterLabel from './FooterLabel';
import FooterLink from './FooterLink';
import FooterSocialLink from './FooterSocialLink';
import Logo from './logo.svg';
import NewsletterForm from './NewsletterForm';

const StyledLogo = styled(Logo)`
  ${theme.mediaQueries.mobileAndTablet} {
    width: 9rem;
  }
`;

const Background = styled.footer`
  background-color: ${theme.colors.epBlack};
  color: ${theme.colors.white};
`;

const FooterContainer = styled(Container)`
  padding: 2.5rem 0 1.5rem;
`;

const LinksWrapper = styled.div`
  display: flex;

  ${theme.mediaQueries.mobileAndTablet} {
    display: none;
  }
`;

const Links = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  :not(:first-of-type) {
    margin-left: 2rem;
  }
`;

const AccordionWrapper = styled.div`
  ${theme.mediaQueries.desktop} {
    display: none;
  }
`;

const Divider = styled.div`
  margin: 2rem 0;
  height: 2px;
  background-color: ${theme.colors.darkGray};

  ${theme.mediaQueries.mobileAndTablet} {
    margin: 4rem 0 1rem;
  }
`;

const SubscribeLabel = styled(FooterLabel)`
  letter-spacing: 1px;
  text-transform: none;
  font-family: ${theme.typography.primaryFont.light};
  margin-left: 2rem;
  ${theme.mediaQueries.mobileAndTablet} {
    width: 100%;
    margin-left: 0rem;
    margin-top: 2rem;
    margin-bottom: 2rem;

    br {
      display: none;
    }
  }
`;

const StyledButton = styled(Button)`
  font-size: 1rem;
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;

  ${theme.mediaQueries.desktop} {
    flex: 1;
    justify-content: flex-end;
  }

  ${theme.mediaQueries.mobileAndTablet} {
    margin: 5rem 0 0 -10px;
    width: 100%;
  }
`;

const FootNote = styled.div`
  display: flex;
  margin-top: 3rem;
  align-items: flex-start;
  font-size: ${theme.typography.toRem(14)};
  line-height: ${theme.typography.toRem(27)};

  ${theme.mediaQueries.mobileAndTablet} {
    margin-top: 2rem;
    flex-direction: column;
  }
`;

const LinksAndCopyright = styled.span`
  font-family: ${theme.typography.footerFont};
  font-size: ${theme.typography.toRem(14)};
  line-height: ${theme.typography.toRem(20)};
  color: ${theme.colors.white};
  ${theme.mediaQueries.desktop} {
    margin-left: 0.5rem;
  }

  ${theme.mediaQueries.mobileAndTablet} {
    margin-top: 1rem;
  }
`;

const Copyright = styled('div')`
  margin-top: 0.5rem;
`;

const FootNoteLink = styled(FooterLink)`
  font-family: ${theme.typography.footerFont};
  font-size: ${theme.typography.toRem(14)};
  line-height: ${theme.typography.toRem(20)};
  color: ${theme.colors.white};

  :not(:first-of-type):before {
    display: inline-block;
    margin: 0 0.5rem;
    content: '|';
  }
`;

const Footer = () => {
  const queryData = useStaticQuery(graphql`
    {
      gcms {
        menu(where: { position: "Footer" }) {
          data
        }
      }

      site {
        siteMetadata {
          socialLinks {
            facebook
            twitter
            youtube
            linkedin
            instagram
          }
          config {
            version
          }
        }
      }
    }
  `);

  const data = queryData.gcms.menu.data as FooterMenuData;
  const socialLinks = queryData.site.siteMetadata.socialLinks as {
    [key: string]: string;
  };

  /* Add build & render versioning for debug deploy sanity */
  var contentDate = new Intl.DateTimeFormat('en', {
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date());

  // const sendToGoogleAnalytics = (label: string) => {
  //   dataLayer.push({ event: 'Footer menu item clicked - ' + label });
  // };

  return (
    <Background>
      <FooterContainer>
        <LinksWrapper>
          {data.map((column, index) => (
            <Links key={index}>
              {column.map(category => (
                <Fragment key={category.label}>
                  <FooterLabel>{category.label}</FooterLabel>
                  {category.submenu?.map(submenu => (
                    <FooterLink
                      key={submenu.label}
                      to={submenu.link}
                      // onClick={event => sendToGoogleAnalytics(submenu.label)} 
                      nofollow={submenu.nofollow}
                    >
                      {submenu.label} 
                    </FooterLink>
                  ))}
                </Fragment>
              ))}
            </Links>
          ))}
        </LinksWrapper>
        <AccordionWrapper>
          <Accordion prefix="footer" data={flatten(data)} footer />
        </AccordionWrapper>
        <Divider />
        <Flex wrap>
          <StyledButton label="Subscribe now" link="/newsletter/" />
          <SubscribeLabel>
            Be an industry insider with EP's <br />
            newsletters and alerts
          </SubscribeLabel>
          <SocialLinks>
            <FooterSocialLink to={socialLinks.facebook} aria-label="Facebook">
              <FacebookIcon />
            </FooterSocialLink>
            <FooterSocialLink to={socialLinks.twitter} aria-label="Twitter">
              <TwitterIcon />
            </FooterSocialLink>
            <FooterSocialLink to={socialLinks.youtube} aria-label="YouTube">
              <YouTubeIcon />
            </FooterSocialLink>
            <FooterSocialLink to={socialLinks.linkedin} aria-label="LinkedIn">
              <LinkedInIcon />
            </FooterSocialLink>
            <FooterSocialLink to={socialLinks.instagram} aria-label="Instagram">
              <InstagramIcon />
            </FooterSocialLink>
          </SocialLinks>
        </Flex>
        <FootNote
          name={queryData.site.siteMetadata.config.version}
          content={contentDate}
        >
          <StyledLogo />
          <LinksAndCopyright>
            <FootNoteLink
              to="/legal/"
              nofollow
              // onClick={event => sendToGoogleAnalytics('Legal')} 
            >
              Legal
            </FootNoteLink>
            <FootNoteLink
              to="/legal/privacy-notice/"
              nofollow
              // onClick={event => sendToGoogleAnalytics('Privacy Notice')} 
            >
              Privacy Notice
            </FootNoteLink>
            <FootNoteLink
              to="/legal/security-features/"
              nofollow
              // onClick={event => sendToGoogleAnalytics('Security')} 
            >
              Security
            </FootNoteLink>
            <Copyright>
              © {new Date().getFullYear()} Entertainment Partners. All rights
              reserved.
            </Copyright>
          </LinksAndCopyright>
        </FootNote>
      </FooterContainer>
    </Background>
  );
};

export default React.memo(Footer);
